import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { IoEye, IoDownload, IoFilter } from 'react-icons/io5'
import { RiShoppingBag3Line } from 'react-icons/ri'

import Button from '@/components/common/Button/Button'
import Spinner from '@/components/common/Spinner/Spinner'
import { useOrders } from '@/hooks/useOrders'
import { sampleProductImage } from '@/utils/constants/common'
import routes from '@/routes/routes'

import './Orders.css'

interface OrderStatus {
    value: string
    label: string
    color: string
}

const orderStatuses: OrderStatus[] = [
    { value: 'all', label: 'All Orders', color: '' },
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'processing', label: 'Processing', color: 'info' },
    { value: 'shipped', label: 'Shipped', color: 'primary' },
    { value: 'delivered', label: 'Delivered', color: 'success' },
    { value: 'cancelled', label: 'Cancelled', color: 'danger' },
]

const Orders = () => {
    const navigate = useNavigate()
    const [statusFilter, setStatusFilter] = useState('all')
    const [dateRange, setDateRange] = useState('all')
    const [showFilters, setShowFilters] = useState(false)
    
    // Convert dateRange to date_from and date_to parameters
    const getDateParams = () => {
        const now = new Date()
        switch (dateRange) {
            case 'last_week':
                const lastWeek = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000)
                return { date_from: lastWeek.toISOString().split('T')[0] }
            case 'last_month':
                const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate())
                return { date_from: lastMonth.toISOString().split('T')[0] }
            case 'last_3_months':
                const last3Months = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate())
                return { date_from: last3Months.toISOString().split('T')[0] }
            default:
                return {}
        }
    }
    
    const { data: orders, isLoading, error } = useOrders({
        status: statusFilter !== 'all' ? statusFilter : undefined,
        ...getDateParams(),
    })

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
        })
    }

    const formatPrice = (price: number) => {
        return `$${price.toFixed(2)}`
    }

    const getStatusBadge = (status: string) => {
        const statusConfig = orderStatuses.find(s => s.value === status)
        return (
            <span className={`status_badge status_${statusConfig?.color || 'secondary'}`}>
                {statusConfig?.label || status}
            </span>
        )
    }

    if (isLoading) {
        return (
            <div className="orders_main_container">
                <div className="orders_loading_container">
                    <Spinner size="lg" />
                    <p className="orders_loading_text">Cargando pedidos...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="orders_main_container">
                <div className="orders_error_container">
                    <div className="orders_error_content">
                        <h2 className="orders_error_title">No se pudieron cargar los pedidos</h2>
                        <p className="orders_error_message">Por favor inténtalo de nuevo más tarde</p>
                        <Button variant="primary" onClick={() => window.location.reload()}>
                            Intentar de nuevo
                        </Button>
                    </div>
                </div>
            </div>
        )
    }

    return (
        <div className="orders_main_container">
            {/* Content Section */}
            <div className="orders_content_wrapper">
                <div className="orders_content_header">
                    <div className="orders_header_text">
                        <h2 className="orders_section_title">Gestionar Pedidos</h2>
                        <p className="orders_section_subtitle">
                            Filtra, busca y administra todos tus pedidos en un solo lugar
                        </p>
                    </div>
                    
                    <div className="orders_header_controls">
                        <Button
                            variant="secondary"
                            onClick={() => setShowFilters(!showFilters)}
                            className="orders_filter_toggle"
                        >
                            <IoFilter size={16} />
                            Filtros
                        </Button>
                    </div>
                </div>

                {/* Filters */}
                {showFilters && (
                    <div className="orders_filters_section">
                        <div className="orders_filters_container">
                            <div className="orders_filters_grid">
                                <div className="orders_filter_group">
                                    <label className="orders_filter_label">Estado</label>
                                    <select
                                        value={statusFilter}
                                        onChange={(e) => setStatusFilter(e.target.value)}
                                        className="orders_filter_select"
                                    >
                                        {orderStatuses.map((status) => (
                                            <option key={status.value} value={status.value}>
                                                {status.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                <div className="orders_filter_group">
                                    <label className="orders_filter_label">Rango de Fecha</label>
                                    <select
                                        value={dateRange}
                                        onChange={(e) => setDateRange(e.target.value)}
                                        className="orders_filter_select"
                                    >
                                        <option value="all">Todo el Tiempo</option>
                                        <option value="last_week">Última Semana</option>
                                        <option value="last_month">Último Mes</option>
                                        <option value="last_3_months">Últimos 3 Meses</option>
                                    </select>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* Results Info */}
                <div className="orders_results_info">
                    {orders && (
                        <div className="orders_results_text">
                            <span className="orders_results_count">
                                Mostrando {orders?.results.length || 0} resultados
                            </span>
                        </div>
                    )}
                </div>

                {/* Orders Grid */}
                {!orders || orders.results.length === 0 ? (
                    <div className="orders_empty_state">
                        <div className="orders_empty_content">
                            <div className="orders_empty_icon">
                                <RiShoppingBag3Line size={64} />
                            </div>
                            <h3 className="orders_empty_title">No se encontraron pedidos</h3>
                            <p className="orders_empty_description">Aún no has realizado ningún pedido. ¡Empieza a comprar para ver tus pedidos aquí!</p>
                            <Button
                                variant="primary"
                                onClick={() => navigate(routes.products)}
                            >
                                Explorar Productos
                            </Button>
                        </div>
                    </div>
                ) : (
                    <div className="orders_grid">
                        {orders.results.map((order) => (
                            <div key={order.id} className="orders_card">
                                <div className="orders_card_header">
                                    <div className="orders_card_info">
                                        <h3 className="orders_card_title">Número de Pedido #{order.order_number}</h3>
                                        <p className="orders_card_date">Realizado el {formatDate(order.created_at)}</p>
                                    </div>
                                    <div className="orders_card_status">
                                        {getStatusBadge(order.status)}
                                    </div>
                                </div>

                                <div className="orders_card_content">
                                    <div className="orders_items_summary">
                                        <span className="orders_items_count">{order.items.length} artículos</span>
                                        <span className="orders_total_amount">Total: {formatPrice(order.total_amount)}</span>
                                    </div>
                                    
                                    <div className="orders_items_preview">
                                        {order.items.slice(0, 3).map((item, index) => (
                                            <div key={index} className="orders_item_preview">
                                                <img
                                                    src={item.product.main_image_url || sampleProductImage}
                                                    alt={item.product.name}
                                                    className="orders_item_image"
                                                />
                                                <div className="orders_item_info">
                                                    <span className="orders_item_name">{item.product.name}</span>
                                                    <span className="orders_item_quantity">Cant: {item.quantity}</span>
                                                </div>
                                            </div>
                                        ))}
                                        {order.items.length > 3 && (
                                            <div className="orders_more_items">
                                                +{order.items.length - 3} más
                                            </div>
                                        )}
                                    </div>
                                </div>

                                <div className="orders_card_actions">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={() => navigate(`${routes.orders}/${order.id}`)}
                                        className="orders_action_button"
                                    >
                                        <IoEye size={16} />
                                        Ver Detalles
                                    </Button>
                                    
                                    {(order as any).invoice_url && (
                                        <Button
                                            variant="secondary"
                                            size="sm"
                                            onClick={() => window.open((order as any).invoice_url, '_blank')}
                                            className="orders_action_button"
                                        >
                                            <IoDownload size={16} />
                                            Descargar Factura
                                        </Button>
                                    )}
                                    
                                    {order.status === 'delivered' && (
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            onClick={() => navigate(`${routes.products}?reorder=${order.id}`)}
                                            className="orders_action_button"
                                        >
                                            Reordenar
                                        </Button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default Orders
